import { fromZonedTime } from "date-fns-tz";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { GroupBase, OptionsOrGroups } from "react-select";
import { toast } from "react-toastify";
import { DateRange } from "rsuite/esm/DateRangePicker";
import { useLazyOrganizationDriversQuery } from "../../../api/network/adminApiServices";
import {
  useLazyOrganizationVehiclesQuery,
  useOrganizationVehiclesQuery,
} from "../../../api/network/adminApiServices";
import {
  exportCSV,
  useGetDeviceEventsListQuery,
  useGetDeviceListingQuery,
  useLazyCameraStatusCodesQuery,
  useLazyGetDeviceListingQuery,
} from "../../../api/network/camerasAdminVMApiService";
import {
  setListingQueryParams,
  TListingQueryParams,
} from "../../../api/store/commonSlice";
import {
  CameraStatusCode,
  DeviceEvent,
  DeviceListingResponse,
} from "../../../api/types/Camera";
import { TLegacyDriver } from "../../../api/types/Driver";
import { ListingResponse } from "../../../api/types/Listing";
import { TLegacyVehicle } from "../../../api/types/Vehicle";
import svgIcons from "../../../assets/svg/svgIcons";
import {
  AdminFormFieldAsyncLazyDropdown,
  AdminFormFieldDropdown,
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import AdminTable from "../../../components/adminTable";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../../components/modalEditListingColumns";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { useAccountTimezone } from "../../../hooks/timezone";
import { routeUrls } from "../../../navigation/routeUrls";
import { asyncDebounce } from "../../../utils/common";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  getStartEndOfDayInUTC,
  jsTimeToDBTime,
} from "../../../utils/dateTime";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { useLoggedInUserData } from "../../../utils/user";
import { HISTORY_EVENTS_STATUS_OPTIONS, RENAMED_EVENTS } from "../constants";
import FormFieldDateRangePicker from "./dateRangePicker";
import CameraVideoHistoryEventsMap from "./video-history-events-map";
import { consistentDisplayAssetItem } from "../../../utils/vehicle";

const LAST_N_DAYS_OPTIONS = [
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 Days", value: "7" },
  { label: "Last 14 Days", value: "14" },
  ...(APP_CONFIG.DEBUG.ALL ? [{ label: "Last 365 Days", value: "365" }] : []),
];
const LAST_N_DAYS_DEFAULT = "yesterday";

type ScreenCameraVideoHistoryEventsProps = {
  displayAsDashWidget?: boolean;
};

const ScreenCameraVideoHistoryEvents = ({
  displayAsDashWidget = false,
}: ScreenCameraVideoHistoryEventsProps) => {
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const all_columns = [
    "Event",
    "Camera ID",
    "Asset ID",
    "Asset Description",
    "Display Name",
    ...(showDriver ? ["Driver"] : []),
    // "Groups",
    "Date & Time",
    "Event Data",
    // "Expires",
  ];
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.cameras",
  });

  const dispatch = useDispatch();

  // retrieve account timezone
  const { convertToTZForDateFns, tzForDisplay, tzForDateFns } =
    useAccountTimezone();

  const userIsImpersonated = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(
    localStorageKeys.columns.CameraVideoHistoryEvents
  );
  const [columns, setColumns] = useState<TListingColumn[]>(
    all_columns.map((colItem, _) => {
      return {
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      };
    })
  );
  const [disableExportCSV, setDisableExportCSV] = useState<boolean>(false);
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const [dashSelectedCameraOption, setDashSelectedCameraOption] =
    useState<TSelectboxOption | null>(null);
  const {
    deviceEvents: deviceEventsListQuery,
    deviceEventsDashboard: deviceEventsListDashboardQuery,
    deviceEventsSelectedCameraOption,
  } = listingQueryParams;

  const applicableDeviceEventsListQuery = displayAsDashWidget
    ? deviceEventsListDashboardQuery
    : deviceEventsListQuery;

  const eventDuration =
    applicableDeviceEventsListQuery.duration ?? LAST_N_DAYS_DEFAULT;

  // helper to format a Date object into "yyyy-MM-dd"
  const formatDate = (date: Date) =>
    [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, "0"),
      String(date.getDate()).padStart(2, "0"),
    ].join("-");

  const from = useMemo(() => {
    const refDateStart = new Date(
      `${formattedDateTimeBasedOnTimezone(Date.now(), tzForDateFns, "yyyy-MM-dd")}T00:00:00`
    );

    if (eventDuration === "yesterday") {
      refDateStart.setDate(refDateStart.getDate() - 1);
    } else if (typeof eventDuration === "number") {
      if (displayAsDashWidget) {
        refDateStart.setDate(refDateStart.getDate() - (eventDuration - 1));
      } else {
        refDateStart.setMonth(
          refDateStart.getMonth() - 1 - (APP_CONFIG.DEBUG.ALL ? 12 : 0)
        );
        refDateStart.setDate(1);
      }
    }
    const { startOfDayInUTC } = getStartEndOfDayInUTC(
      formatDate(refDateStart),
      tzForDateFns
    );
    if (APP_CONFIG.DEBUG.ALL) console.log("from updated", startOfDayInUTC);
    return jsTimeToDBTime(startOfDayInUTC.getTime());
  }, [eventDuration, displayAsDashWidget, tzForDateFns]);

  const to = useMemo(() => {
    const refDateEnd = new Date(
      `${formattedDateTimeBasedOnTimezone(Date.now(), tzForDateFns, "yyyy-MM-dd")}T00:00:00`
    );
    if (eventDuration === "yesterday") {
      refDateEnd.setDate(refDateEnd.getDate() - 1);
    } else if (typeof eventDuration !== "number" && !displayAsDashWidget) {
      refDateEnd.setMonth(refDateEnd.getMonth() + 1);
      refDateEnd.setDate(0);
    }
    const { endOfDayInUTC } = getStartEndOfDayInUTC(
      formatDate(refDateEnd),
      tzForDateFns
    );
    return jsTimeToDBTime(endOfDayInUTC.getTime());
  }, [eventDuration, displayAsDashWidget, tzForDateFns]);

  // Commented to persist filter state when navigating back from event details
  // useEffect(() => {
  //   // reset dashboard query params on unmount
  //   return () => {
  //     if (!displayAsDashWidget) return;
  //     dispatch(
  //       setListingQueryParams({
  //         ...listingQueryParams,
  //         deviceEventsDashboard: {
  //           page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
  //           page: 1,
  //           serial_number: "",
  //           statuscodes: "",
  //           start_time: null, // null helps identify initialization
  //           end_time: null, // null helps identify initialization
  //         },
  //         deviceEventsSelectedCameraOption: null,
  //       })
  //     );
  //   };
  // }, [displayAsDashWidget]);

  useEffect(() => {
    if (
      applicableDeviceEventsListQuery.start_time === null &&
      displayAsDashWidget
    ) {
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          [displayAsDashWidget ? "deviceEventsDashboard" : "deviceEvents"]: {
            ...applicableDeviceEventsListQuery,
            // if start_time is not null, we assume list query is initialized
            start_time: from,
            end_time: to,
          },
        })
      );
    }
  }, [
    applicableDeviceEventsListQuery /*, from, to -- disabled as we don't want query to update if once initialized */,
    ,
    displayAsDashWidget,
  ]);
  useEffect(() => {
    if (APP_CONFIG.DEBUG.ALL) {
      console.log("useEffect from CHANGED =======");
      console.log(
        listingQueryParams?.[
          displayAsDashWidget ? "deviceEventsDashboard" : "deviceEvents"
        ]
      );
    }
    if (
      displayAsDashWidget ||
      applicableDeviceEventsListQuery.start_time === null
    ) {
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          [displayAsDashWidget ? "deviceEventsDashboard" : "deviceEvents"]: {
            ...applicableDeviceEventsListQuery,
            start_time: from,
            end_time: to,
            page: applicableDeviceEventsListQuery.page || 1,
          },
        })
      );
    }
  }, [
    from,
    to,
    displayAsDashWidget,
    dispatch,
    applicableDeviceEventsListQuery.start_time,
  ]);

  const [cameraDeviceListForSelectLabel, setCameraDeviceListForSelectLabel] =
    useState<TSelectboxOption[]>([]);

  const commonDeviceListingParams = {
    keyword: "",
    model: "",
    status: "",
    offset: 0,
    limit: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
    page: 1,
    linked_to_asset: 1,
  };

  // introduced to maintain a record of all devices, and extract cid of selected camera
  const [allDevicesList, setAllDevicesList] = useState<DeviceListingResponse[]>(
    []
  );

  // fetching camera device listing
  const {
    data: dataInitialDeviceListing,
    isFetching: isFetchingInitialDeviceListing,
    error,
    refetch,
  } = useGetDeviceListingQuery(commonDeviceListingParams, {
    skip: displayAsDashWidget,
    refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
  });
  const applicableSelectedCameraOption = useMemo(() => {
    return displayAsDashWidget
      ? dashSelectedCameraOption
      : deviceEventsSelectedCameraOption;
  }, [
    displayAsDashWidget,
    dashSelectedCameraOption,
    deviceEventsSelectedCameraOption,
  ]);
  useEffect(() => {
    if (
      !displayAsDashWidget &&
      !!dataInitialDeviceListing &&
      !isFetchingInitialDeviceListing &&
      applicableSelectedCameraOption === null
    ) {
      setAllDevicesList((prev) => [
        ...prev,
        ...(dataInitialDeviceListing?.data.filter(
          (item: DeviceListingResponse) =>
            !!item.serial_number &&
            !prev.some(
              (existing) => existing.serial_number === item.serial_number
            )
        ) || []),
      ]);
      const firstItem = dataInitialDeviceListing?.data?.[0];

      const initiallySelectedItem = !!firstItem?.serial_number
        ? {
            value: firstItem?.serial_number,
            label: consistentDisplayAssetItem(
              firstItem?.ft_device_id,
              firstItem?.ft_device_description,
              firstItem?.ft_displayname,
              firstItem?.drivername,
              showDriver
            ),
          }
        : null;
      // setSelectedCameraDeviceOption(initiallySelectedItem);
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          ...(displayAsDashWidget
            ? {}
            : {
                deviceEventsSelectedCameraOption: initiallySelectedItem,
              }),
        })
      );
      if (displayAsDashWidget)
        setDashSelectedCameraOption(initiallySelectedItem);
    }
  }, [
    displayAsDashWidget,
    dataInitialDeviceListing,
    isFetchingInitialDeviceListing,
    applicableSelectedCameraOption,
    deviceEventsSelectedCameraOption,
    // dashSelectedCameraOption,
  ]);

  const [
    getDeviceListingAPITrigger,
    {
      isLoading: isLoadingLazyDeviceListing,
      isFetching: isFetchingLazyDeviceListing,
    },
  ] = useLazyGetDeviceListingQuery();
  const loadOptionsHandlerDevice = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: deviceListingResponse } = await getDeviceListingAPITrigger({
      ...commonDeviceListingParams,
      keyword: inputValue,
      //search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.ALL_FLAG, // TODO: search_fields to be implemented for camera listing API
      // page: additional?.page || 1,
      offset:
        (additional?.page || 1) > 1
          ? (additional?.page || 1) * commonDeviceListingParams.limit -
            commonDeviceListingParams.limit
          : 0,
    });

    if (!deviceListingResponse || !deviceListingResponse?.data) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 1,
        },
      };
    }
    const { data: results } = deviceListingResponse as {
      data: DeviceListingResponse[];
    };
    setAllDevicesList((prev) => [
      ...prev,
      ...(results || []).filter(
        (item: DeviceListingResponse) =>
          !!item.serial_number &&
          !prev.some(
            (existing) => existing.serial_number === item.serial_number
          )
      ),
    ]);
    const options = !!results
      ? (results || ([] as DeviceListingResponse[])).map(
          (item: DeviceListingResponse, index: number) =>
            ({
              value: `${item.serial_number}`,
              // label: `${item.unit_name}${item?.ft_device_id ? ` | ${item.ft_device_id}` : ""}${item?.ft_device_description ? ` | ${item.ft_device_description}` : ""}${item?.displayname ? ` | ${item.displayname}` : ""}`,
              label: `${consistentDisplayAssetItem(item.ft_device_id, item.ft_device_description, item.ft_displayname, item.drivername, showDriver)}`,
            }) as TSelectboxOption
        )
      : [];
    setCameraDeviceListForSelectLabel([
      ...cameraDeviceListForSelectLabel,
      // ensure unique values
      ...(options as TSelectboxOption[]).filter(
        (option) =>
          !cameraDeviceListForSelectLabel.some(
            (existingOption) => existingOption.value === option.value
          )
      ),
    ]);
    return {
      options,
      hasMore: false, // TODO: NEED TO CHECK // deviceListingResponse?.next ? true : false,
      additional: {
        page: (additional?.page as number) + 1 || 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerDevice = asyncDebounce(
    loadOptionsHandlerDevice,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  const { data: dataDeviceEventsList, isFetching: isFetchingDeviceEventsList } =
    useGetDeviceEventsListQuery(
      {
        ...applicableDeviceEventsListQuery,
        serial_number: applicableSelectedCameraOption?.value || "",
      },
      {
        skip:
          applicableDeviceEventsListQuery.start_time !== null
            ? displayAsDashWidget
              ? false
              : !applicableSelectedCameraOption
            : true, // skip if not initialized or if displayAsDashWidget is true
      }
    );

  const { count, results } = dataDeviceEventsList || {};
  //table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || []).map((item, index) => ({
        navLink: item.recordid
          ? `${routeUrls.dashboardChildren.cameraChildren.events}/${applicableSelectedCameraOption?.value || item.cameraid}__${item.recordid}`
          : "",
        tableRowId: `event-${item.recordid}`,
        stateData: {
          ...item,
          cameraListItem: allDevicesList.find(
            (d) => d.serial_number === item.cameraid
          ),
        },
        key: `event-${item.recordid}-${index}`,
        cellData: [
          !!item.status.description
            ? RENAMED_EVENTS.find(
                (_item) => _item.backendValue === item.status.description
              )?.displayValue || item.status.description
            : "", // "Event"
          // item.status.description || "", // "Event"

          item.cameraid, // "Camera ID"
          item.deviceid, // "Assets ID",
          item.device_description,
          item.displayname,
          ...(showDriver ? [item.drivername || item.driverid || "-"] : []), // "Driver"
          // "N/A", // "Groups"
          !!item?.timestamp && !!tzForDateFns
            ? formattedDateTimeBasedOnTimezone(
                item?.timestamp * 1000,
                tzForDateFns
              )
            : "-", // "Date & Time"
          !!item.ticket ? "Uploaded" : "Not uploaded", // "Event Data"
          // "N/A", // "Expires"
        ].filter((_, index) => columns[index].show),
      }))
    : [];

  // INTENTIONALLY using vehicle API to get drivers as here we will need to show the drivers
  // from those vehicles that have a camera associated with them
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  // const {
  //   data: dataInitialCameraVehicleDriversList,
  //   isFetching: isFetchingInitialCameraVehicleDriversList,
  // } = useOrganizationVehiclesQuery({
  //   accountid: `${thisUserOrganizationId}`,
  //   page: 1,
  //   page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
  //   search: "",
  //   linked_to_camera: true,
  //   linked_to_driver: true,
  // });
  // const [orgVehiclesQuery, { isLoading: isLazyLoadingVehicles }] =
  //   useLazyOrganizationVehiclesQuery();
  // Update 8/21/2025 - reverting from vehicles API back to drivers API for events page
  const [orgDriversQuery, { isLoading: isLazyLoadingDrivers }] =
    useLazyOrganizationDriversQuery();
  const loadOptionsHandlerDriver = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: cameraVehicleDriversQueryResponse } = await orgDriversQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page ?? 1,
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.DRIVER_DROPDOWN.join(","), // search by driverid only
      isactive: 1,
      // linked_to_camera: true,
      // linked_to_driver: true,
    });

    if (
      !cameraVehicleDriversQueryResponse ||
      !cameraVehicleDriversQueryResponse?.results
    ) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: additional?.page || 1,
        },
      };
    }
    const { results } = cameraVehicleDriversQueryResponse as {
      results: TLegacyDriver[];
    };
    return {
      options: !!results
        ? (results || ([] as TLegacyDriver[])).map(
            (item: TLegacyDriver, index: number) =>
              ({
                value: `${item.driverid}`,
                label: `${item.description || item.driverid}`, // driverDisplayName(item), //  || item.displayname
              }) as TSelectboxOption
          )
        : [],
      hasMore: cameraVehicleDriversQueryResponse?.next ? true : false,
      additional: {
        page: additional?.page ? (additional?.page as number) + 1 : 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerDriver = asyncDebounce(
    loadOptionsHandlerDriver,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  const [
    cameraStatusCodesQuery,
    { isLoading: isLazyLoadingCameraStatusCodes },
  ] = useLazyCameraStatusCodesQuery();
  const loadOptionsHandlerCameraStatusCodes = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    // console.log("CALL prevOptions", prevOptions);
    const { data: cameraVehicleDriversQueryResponse } =
      await cameraStatusCodesQuery({
        page: additional?.page ?? 1,
        page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
        search: inputValue,
      });

    const results = cameraVehicleDriversQueryResponse;
    return {
      options: !!results
        ? (results || ([] as CameraStatusCode[]))
            // as the API returns all results, filtration needs to be done at frontend level
            .filter((resultItem) =>
              inputValue === ""
                ? true
                : resultItem.description
                    .toLocaleLowerCase()
                    .includes(inputValue.toLowerCase()) ||
                  resultItem.statuscode
                    .toString()
                    .toLocaleLowerCase()
                    .includes(inputValue.toLowerCase())
            )
            .map(
              // (item: CameraStatusCode, index: number) =>
              //   ({
              //     value: item.statuscode.toString(),
              //     label: item.description.includes("Unknown")
              //       ? `${item.description} | ${item.statuscode.toString()}`
              //       : item.description,
              //   }) as TSelectboxOption

              (item: CameraStatusCode) => {
                const renamed = RENAMED_EVENTS.find(
                  (r) => r.backendValue === item.description
                );
                return {
                  value: item.statuscode.toString(),
                  label: renamed ? renamed.displayValue : item.description,
                } as TSelectboxOption;
              }
            )
        : [],
      hasMore: false, // the API returns complete list of codes in a single call
      additional: {
        page: additional?.page ? (additional?.page as number) + 1 : 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerCameraStatusCodes = asyncDebounce(
    loadOptionsHandlerCameraStatusCodes,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  const showClearButton =
    !!applicableDeviceEventsListQuery?.statuscodes ||
    (!!displayAsDashWidget && applicableSelectedCameraOption !== null) ||
    !!applicableDeviceEventsListQuery?.driver_id;

  const MemoizedCameraVideoHistoryEventsMap = useMemo(() => {
    return (
      <CameraVideoHistoryEventsMap
        dataDeviceEventsList={
          !!dataDeviceEventsList
            ? ({
                ...dataDeviceEventsList,
                results: dataDeviceEventsList.results.filter(
                  (eventItem) => !!eventItem.latitude && !!eventItem.longitude
                ),
              } as ListingResponse<DeviceEvent[]>)
            : undefined
        }
        isFetchingDeviceEventsList={isFetchingDeviceEventsList}
      />
    );
  }, [dataDeviceEventsList, isFetchingDeviceEventsList]);

  const handleRangeChange = (value: [any, any] | null) => {
    if (value) {
      const [start, end] = value;
      if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
        // Invalid date detected, abort further logic
        return;
      }
      const utcStartDate = fromZonedTime(
        start,
        convertToTZForDateFns(tzForDateFns)
      );
      const jsTimeStart = new Date(utcStartDate).getTime();
      const utcEndDate = fromZonedTime(
        end,
        convertToTZForDateFns(tzForDateFns)
      );
      const jsTimeEnd = new Date(utcEndDate).getTime();
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          [displayAsDashWidget ? "deviceEventsDashboard" : "deviceEvents"]: {
            ...applicableDeviceEventsListQuery,
            start_time: jsTimeToDBTime(jsTimeStart),
            end_time: jsTimeToDBTime(jsTimeEnd),
            page: 1,
          },
        })
      );
    }
  };

  const handleExportCSV = async () => {
    setDisableExportCSV(true);
    try {
      const blob = await exportCSV({
        ...applicableDeviceEventsListQuery,
        serial_number: applicableSelectedCameraOption?.value || "",
        csv_export: true,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // link.setAttribute("download", "device-events.csv"); // optional: change file name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t("csv_download_successful"));
    } catch (error) {
      console.error("CSV download failed:", error);
    } finally {
      setDisableExportCSV(false);
    }
  };

  return (
    <div
      className={
        displayAsDashWidget
          ? `flex flex-col rounded-lg h-[calc(50vh)] overflow-hidden relative bg-white border-2 shadow-lg`
          : `flex flex-col rounded-lg ${userIsImpersonated ? "h-screen" : "h-screen"} overflow-hidden relative`
      }
    >
      {/* map */}
      {displayAsDashWidget !== true && (
        <div className="h-1/2">{MemoizedCameraVideoHistoryEventsMap}</div>
      )}
      {displayAsDashWidget && APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <pre className="absolute top-0 right-10 bg-amber-500">
          video-history-events.tsx
        </pre>
      )}

      {/* listing */}
      <div
        className={`${displayAsDashWidget ? "h-full" : "h-1/2 bg-gray-100"} flex flex-col px-5 pt-4`}
      >
        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
          <pre className="absolute text-[10px] z-10 bg-white/30 -translate-y-full">
            {JSON.stringify(
              {
                ...applicableDeviceEventsListQuery,
                serial_number: applicableSelectedCameraOption?.value || "",
              },
              null,
              2
            )}
          </pre>
        )}
        <div className="w-full flex gap-4">
          {displayAsDashWidget && (
            <h4 className="flex-grow text-lg font-semibold flex items-center">
              {t("widget_title")}
            </h4>
          )}
          <AdminFormFieldAsyncLazyDropdown
            key={`selected_${JSON.stringify(applicableSelectedCameraOption?.value || {})}`}
            loadingData={
              isFetchingInitialDeviceListing ||
              isLoadingLazyDeviceListing ||
              isFetchingLazyDeviceListing
            }
            label={false}
            id="camera_device_id"
            name="camera_device_id"
            placeholder={"Search..."}
            // TODO: NEEDS FIXING - enabling this is causing page 1 to show 2 times
            // defaultOptions={
            //   dataInitialDeviceListing?.data?.map((cameraDevice) => ({
            //     value: cameraDevice?.serial_number,
            //     label: cameraDevice?.unit_name,
            //   })) || [] // Ensuring a fallback to an empty array if results are undefined
            // }
            additional={{ page: 1 }}
            defaultOption={
              !!applicableSelectedCameraOption
                ? applicableSelectedCameraOption.value === "-"
                  ? undefined
                  : applicableSelectedCameraOption
                : undefined
            }
            loadOptions={debouncedLoadOptionsHandlerDevice}
            onChange={(e: any) => {
              // setSelectedCameraDeviceOption({
              //   value: e?.value,
              //   label: e?.label,
              // } as TSelectboxOption);
              dispatch(
                setListingQueryParams({
                  ...listingQueryParams,
                  ...(displayAsDashWidget
                    ? {}
                    : {
                        deviceEventsSelectedCameraOption: e,
                      }),
                })
              );
              if (displayAsDashWidget) setDashSelectedCameraOption(e);
            }}
            // onBlur={(e: any) => {
            //   formikSetTouched("vehicle_id", true);
            //   handleBlur(e);
            // }}
            // touched={touched.vehicle_id}
            // error={errors.vehicle_id}
            disabled={
              applicableSelectedCameraOption === null &&
              isFetchingInitialDeviceListing
            }
            // detailsFormField={true}
            customWrapperClass="w-[calc(25%)] mt-1"
          />
          {displayAsDashWidget ? (
            <AdminFormFieldDropdown
              id="duration-select"
              label=""
              name={t("select_duration")}
              // placeholder={t("select_diration_placeholder")}
              options={LAST_N_DAYS_OPTIONS}
              value={eventDuration.toString()}
              onChange={(option) => {
                if (option) {
                  console.log("option", option);
                  const isNumber = !isNaN(parseInt(option.value, 10));
                  const newValue = isNumber
                    ? parseInt(option.value, 10)
                    : option.value;

                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      [displayAsDashWidget
                        ? "deviceEventsDashboard"
                        : "deviceEvents"]: {
                        ...applicableDeviceEventsListQuery,
                        duration: newValue,
                        page: 1,
                      },
                    })
                  );
                }
              }}
              customWrapperClass={displayAsDashWidget ? "w-40" : "flex-grow"}
              dimensionClass="w-full h-11"
              key={`duration-select-${eventDuration.toString()}`}
            />
          ) : (
            <FormFieldDateRangePicker
              className="mt-1 flex h-11"
              value={
                applicableDeviceEventsListQuery?.start_time &&
                applicableDeviceEventsListQuery?.end_time
                  ? ([
                      //TODO: need fixing
                      new Date(
                        formattedDateTimeBasedOnTimezone?.(
                          dbTimeToJsTime(
                            applicableDeviceEventsListQuery?.start_time
                          ),
                          tzForDateFns,
                          APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
                        )
                      ),
                      new Date(
                        formattedDateTimeBasedOnTimezone?.(
                          dbTimeToJsTime(
                            applicableDeviceEventsListQuery?.end_time
                          ),
                          tzForDateFns,
                          APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
                        )
                      ),
                      // new Date(dbTimeToJsTime(fileRetrievalQueryParams?.since)),
                      // new Date(dbTimeToJsTime(fileRetrievalQueryParams?.until)),
                    ] as DateRange)
                  : null
              }
              onChange={handleRangeChange}
            />
          )}
          <AdminFormFieldAsyncLazyDropdown
            key={`status-select-${applicableDeviceEventsListQuery?.statuscodes}`}
            customWrapperClass={`mt-1 ${displayAsDashWidget ? "w-48" : "flex-grow"}`}
            dimensionClass="w-full h-11"
            loadingData={isLazyLoadingCameraStatusCodes}
            label={false}
            id="status-select"
            name={t("select_event_name")}
            placeholder={t("select_event_placeholder")}
            value={
              !!applicableDeviceEventsListQuery?.statuscodes
                ? applicableDeviceEventsListQuery?.statuscodes
                : undefined
            }
            defaultOption={
              !!applicableDeviceEventsListQuery.statuscodes
                ? {
                    value: applicableDeviceEventsListQuery.statuscodes,
                    label: `${applicableDeviceEventsListQuery.statuscodeslabel}`,
                  }
                : undefined
            } // passed undefined to let placeholder display if no value is selected
            additional={{ page: 1 }}
            isClearable={true}
            HideDropdownIndicator={false}
            loadOptions={debouncedLoadOptionsHandlerCameraStatusCodes}
            noOptionsMessage={() => t("select_event_no_item")}
            onChange={(option) => {
              dispatch(
                setListingQueryParams({
                  ...listingQueryParams,
                  [displayAsDashWidget
                    ? "deviceEventsDashboard"
                    : "deviceEvents"]: {
                    ...applicableDeviceEventsListQuery,
                    statuscodes: option?.value || "",
                    statuscodeslabel: option?.label,
                    page: 1,
                  },
                })
              );
            }}
          />
          {showDriver && (
            <AdminFormFieldAsyncLazyDropdown
              key={`driver_id_filter_${JSON.stringify(applicableDeviceEventsListQuery)}`}
              customWrapperClass="flex-grow min-w-36 mt-1"
              loadingData={isLazyLoadingDrivers}
              label={false}
              id="driver_id"
              name="driver_id"
              value={
                !!applicableDeviceEventsListQuery?.driver_id
                  ? applicableDeviceEventsListQuery.driver_id
                  : undefined
              } // passed undefined to let placeholder display if no value is selected
              placeholder={t("driver_id_placeholder")}
              // defaultOptions={
              //   dataInitialCameraVehicleDriversList?.results?.map(
              //     (item: TLegacyVehicle) => ({
              //       value: `${item.driverid}`,
              //       label: `${item.driverid}${item.drivername}`, // driverDisplayName(item),
              //     })
              //   ) || undefined
              // }
              defaultOption={
                !!applicableDeviceEventsListQuery.driver_id
                  ? {
                      value: applicableDeviceEventsListQuery.driver_id,
                      label:
                        applicableDeviceEventsListQuery.driver_name ||
                        applicableDeviceEventsListQuery.driver_id ||
                        "-", // values.driver_name ?? values.driver_id,
                    }
                  : undefined
              } // passed undefined to let placeholder display if no value is selected
              additional={{ page: 1 }}
              isClearable={true}
              HideDropdownIndicator={false}
              loadOptions={debouncedLoadOptionsHandlerDriver}
              noOptionsMessage={() => t("driver_id_no_item")}
              onChange={(e) => {
                if (APP_CONFIG.DEBUG.ALL) console.log("driver_id", e);
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    [displayAsDashWidget
                      ? "deviceEventsDashboard"
                      : "deviceEvents"]: {
                      ...applicableDeviceEventsListQuery,
                      driver_id: e?.value || undefined,
                      driver_name: e?.label || undefined,
                      page: 1,
                    },
                  })
                );
              }}
            />
          )}
          {showClearButton && (
            <AdminFormFieldSubmit
              buttonDimensionClass={"h-11 w-full px-2 group"}
              customWrapperClass="w-28 mt-1"
              type="button"
              variant="primary"
              label={
                <span className="flex justify-center whitespace-nowrap">
                  {t("clear")}
                  {
                    <img
                      className={`ml-2 ${!!showClearButton ? "transition-all group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert" : ""} h-5 w-5`}
                      src={
                        !showClearButton
                          ? svgIcons.CloseIconGray
                          : svgIcons.CloseIcon
                      }
                      alt={"clear"}
                    />
                  }
                </span>
              }
              onClick={() => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    [displayAsDashWidget
                      ? "deviceEventsDashboard"
                      : "deviceEvents"]: {
                      ...applicableDeviceEventsListQuery,
                      statuscodes: "",
                      driver_id: undefined,
                      driver_name: undefined,
                      page: 1,
                      ...(displayAsDashWidget ? { serial_number: "" } : {}),
                    },
                    ...(displayAsDashWidget
                      ? {}
                      : {
                          deviceEventsSelectedCameraOption: null,
                        }),
                  })
                );
                if (displayAsDashWidget) setDashSelectedCameraOption(null);
              }}
              disabled={!showClearButton}
            />
          )}
          {displayAsDashWidget ? null : (
            <AdminFormFieldSubmit
              buttonDimensionClass={"h-11 w-full px-2 group"}
              customWrapperClass="w-28 mt-1"
              type="button"
              variant="primary-like"
              label={
                <span className="flex justify-center whitespace-nowrap">
                  {t("export")}
                  <img
                    className={`ml-2 ${
                      !applicableSelectedCameraOption ||
                      isFetchingDeviceEventsList ||
                      disableExportCSV !== true
                        ? "transition-all group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"
                        : ""
                    }`}
                    src={svgIcons.Upload}
                    alt={t("export")}
                  />
                </span>
              }
              onClick={handleExportCSV}
              disabled={
                !applicableSelectedCameraOption ||
                isFetchingDeviceEventsList ||
                disableExportCSV
              }
            />
          )}
        </div>
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.CameraVideoHistoryEvents}
          modalType="showEditColumns"
        />
        <div className="flex-1 flex-grow flex flex-col py-4 relative">
          <EditListingColumnsButton modalType="showEditColumns" />
          <div className="flex-grow relative">
            <AdminTable
              customWrapperClass="overflow-auto absolute top-0 left-0 w-full h-full"
              // viewportHeightOffset={"400"}
              columns={columns
                .filter((item) => item?.show)
                .map((item) => item.name)}
              data={tableData}
              isLoading={isFetchingDeviceEventsList}
              posAbsoluteLoader={true}
            />
          </div>
          {!isFetchingInitialDeviceListing && (
            <Pagination
              pageSize={applicableDeviceEventsListQuery.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    [displayAsDashWidget
                      ? "deviceEventsDashboard"
                      : "deviceEvents"]: {
                      ...applicableDeviceEventsListQuery,
                      page: 1,
                      page_size: parseInt(`${e?.value}`),
                    },
                  })
                );
              }}
              totalPages={
                count
                  ? Math.ceil(count / applicableDeviceEventsListQuery.page_size)
                  : 1
              }
              forcePage={applicableDeviceEventsListQuery.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    [displayAsDashWidget
                      ? "deviceEventsDashboard"
                      : "deviceEvents"]: {
                      ...applicableDeviceEventsListQuery,
                      page: data?.selected + 1,
                    },
                  })
                );
              }}
              paginCurrentPage={applicableDeviceEventsListQuery.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
              wrapperClass={`flex mt-3 items-center`}
              resultCountPrefixAtBottomLeft={
                displayAsDashWidget ? t("results") : false
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ScreenCameraVideoHistoryEvents;
